import { z } from 'zod';
import {
  GROUP_CONTENT_ARTICLE_SORTS,
  GROUP_CONTENT_MAX_COUNT,
  GROUP_CONTENT_MIN_COUNT,
  GROUP_CONTENT_MODES,
  GROUP_CONTENT_TYPES,
  GROUP_CONTENT_VIDEO_SORTS,
} from '../types/group-content';

// Same slug ruleset as posts/categories: Thai or lowercase Latin + digits,
// dash-separated (no leading/trailing dash, no double dashes).
const slugRx = /^[฀-๿a-z0-9]+(?:-[฀-๿a-z0-9]+)*$/u;

const slugSchema = z
  .string()
  .trim()
  .min(1, 'กรุณาใส่ slug')
  .max(80)
  .regex(slugRx, 'slug ใช้ได้: ไทย / a-z / 0-9 และ - เท่านั้น');

const nameSchema = z.string().trim().min(1, 'กรุณาใส่ชื่อ').max(120);

/* ---------- Config sub-schemas (per type+mode) ---------- */

const articleAutoConfigSchema = z.object({
  kind: z.literal('article-auto'),
  categorySlug: z
    .string()
    .trim()
    .max(80)
    .nullable()
    .transform((v) => (v === '' || v === null ? null : v)),
  sort: z.enum(GROUP_CONTENT_ARTICLE_SORTS),
});

const articleManualConfigSchema = z.object({
  kind: z.literal('article-manual'),
  items: z.array(z.object({ postId: z.string().min(1).max(40) })).max(GROUP_CONTENT_MAX_COUNT),
});

const videoAutoConfigSchema = z.object({
  kind: z.literal('video-auto'),
  playlistId: z
    .string()
    .trim()
    .max(40)
    .nullable()
    .transform((v) => (v === '' || v === null ? null : v)),
  sort: z.enum(GROUP_CONTENT_VIDEO_SORTS),
});

const videoManualConfigSchema = z.object({
  kind: z.literal('video-manual'),
  items: z.array(z.object({ videoId: z.string().min(1).max(40) })).max(GROUP_CONTENT_MAX_COUNT),
});

const urlManualItemSchema = z.object({
  title: z.string().trim().min(1, 'กรุณาใส่หัวข้อ').max(255),
  url: z.string().trim().url('URL ไม่ถูกต้อง').max(2000),
  imageUrl: z
    .string()
    .trim()
    .max(2000)
    .nullable()
    .transform((v) => (v === '' || v === null ? null : v))
    .refine((v) => v === null || /^https?:\/\//.test(v), { message: 'imageUrl ต้องเป็น http(s)' }),
  summary: z
    .string()
    .trim()
    .max(500)
    .nullable()
    .transform((v) => (v === '' || v === null ? null : v)),
  embed: z
    .string()
    .trim()
    .max(4000)
    .nullable()
    .transform((v) => (v === '' || v === null ? null : v)),
});

const urlManualConfigSchema = z.object({
  kind: z.literal('url-manual'),
  items: z.array(urlManualItemSchema).max(GROUP_CONTENT_MAX_COUNT),
});

const streamingManualItemSchema = z.object({
  url: z.string().trim().url('URL ไม่ถูกต้อง').max(2000),
});

const streamingManualConfigSchema = z.object({
  kind: z.literal('streaming-manual'),
  items: z.array(streamingManualItemSchema).max(GROUP_CONTENT_MAX_COUNT),
});

// Customized items pick their own type — each row is a discriminated
// union on `itemType`. Minimal payload per kind so the editor stays
// compact: article/video reference entities by id, url has title+url
// only (full url cards live in type=url), streaming carries url only.
const customizedManualItemSchema = z.discriminatedUnion('itemType', [
  z.object({
    itemType: z.literal('article'),
    postId: z.string().min(1).max(40),
  }),
  z.object({
    itemType: z.literal('video'),
    videoId: z.string().min(1).max(40),
  }),
  z.object({
    itemType: z.literal('url'),
    title: z.string().trim().min(1, 'กรุณาใส่หัวข้อ').max(255),
    url: z.string().trim().url('URL ไม่ถูกต้อง').max(2000),
  }),
  z.object({
    itemType: z.literal('streaming'),
    url: z.string().trim().url('URL ไม่ถูกต้อง').max(2000),
  }),
]);

const customizedManualConfigSchema = z.object({
  kind: z.literal('customized-manual'),
  items: z.array(customizedManualItemSchema).max(GROUP_CONTENT_MAX_COUNT),
});

export const groupContentConfigSchema = z.discriminatedUnion('kind', [
  articleAutoConfigSchema,
  articleManualConfigSchema,
  videoAutoConfigSchema,
  videoManualConfigSchema,
  urlManualConfigSchema,
  streamingManualConfigSchema,
  customizedManualConfigSchema,
]);

export type GroupContentConfigInput = z.infer<typeof groupContentConfigSchema>;

/* ---------- Cross-field guards ---------- */

function applyCrossFieldRules(
  data: {
    type: 'article' | 'video' | 'url' | 'streaming' | 'customized';
    mode: 'auto' | 'manual';
    maxCount: number;
    config: GroupContentConfigInput;
  },
  ctx: z.RefinementCtx,
) {
  const expectedKind = `${data.type}-${data.mode}` as const;
  if (data.config.kind !== expectedKind) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['config', 'kind'],
      message: `config.kind ต้องเป็น "${expectedKind}" (ตามค่า type+mode)`,
    });
    return;
  }
  // url + streaming + customized are all manual-only — there's no
  // "auto" source to pull from (editor-authored or per-item-typed).
  if (
    (data.type === 'url' || data.type === 'streaming' || data.type === 'customized') &&
    data.mode !== 'manual'
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['mode'],
      message: `type=${data.type} รองรับเฉพาะ mode=manual`,
    });
  }
  // video-auto sort ('latest' | 'playlist') is enum-validated in the config
  // schema; no extra cross-field rule needed.
  // Manual modes: items.length must fit within maxCount.
  if ('items' in data.config && data.config.items.length > data.maxCount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['config', 'items'],
      message: `จำนวน items (${data.config.items.length}) เกิน maxCount (${data.maxCount})`,
    });
  }
}

/* ---------- Create / Update ---------- */

export const createGroupContentSchema = z
  .object({
    name: nameSchema,
    /** Auto-generated from name when omitted. */
    slug: slugSchema.optional(),
    type: z.enum(GROUP_CONTENT_TYPES),
    mode: z.enum(GROUP_CONTENT_MODES),
    maxCount: z.coerce.number().int().min(GROUP_CONTENT_MIN_COUNT).max(GROUP_CONTENT_MAX_COUNT),
    config: groupContentConfigSchema,
  })
  .superRefine((data, ctx) => applyCrossFieldRules(data, ctx));

export type CreateGroupContentInput = z.infer<typeof createGroupContentSchema>;

// PATCH semantics: every field optional, but if `config` or `type`/`mode`/
// `maxCount` are provided we re-run the cross-field rules on the merged
// "incoming" view. Caller is expected to send the full new triple
// (type, mode, config) on any config change — partial config updates aren't
// supported (the discriminated union shape makes a partial-of-partial
// ambiguous; force a full replace instead).
export const updateGroupContentSchema = z
  .object({
    name: nameSchema.optional(),
    slug: slugSchema.optional(),
    type: z.enum(GROUP_CONTENT_TYPES).optional(),
    mode: z.enum(GROUP_CONTENT_MODES).optional(),
    maxCount: z.coerce
      .number()
      .int()
      .min(GROUP_CONTENT_MIN_COUNT)
      .max(GROUP_CONTENT_MAX_COUNT)
      .optional(),
    config: groupContentConfigSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.config !== undefined) {
      if (data.type === undefined || data.mode === undefined || data.maxCount === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['config'],
          message: 'การแก้ config ต้องส่ง type, mode, maxCount มาพร้อมกัน',
        });
        return;
      }
      applyCrossFieldRules(
        {
          type: data.type,
          mode: data.mode,
          maxCount: data.maxCount,
          config: data.config,
        },
        ctx,
      );
    }
  });

export type UpdateGroupContentInput = z.infer<typeof updateGroupContentSchema>;

/* ---------- List query ---------- */

export const listGroupContentsQuerySchema = z.object({
  q: z.string().trim().max(80).optional(),
  type: z.enum(GROUP_CONTENT_TYPES).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

export type ListGroupContentsQuery = z.infer<typeof listGroupContentsQuerySchema>;

/* ---------- Slug-availability check ---------- */

export const groupContentSlugCheckSchema = z.object({
  slug: slugSchema,
  /** Provide on edit so the row's own slug doesn't conflict with itself. */
  excludeId: z.string().min(1).optional(),
});

export type GroupContentSlugCheck = z.infer<typeof groupContentSlugCheckSchema>;
